import { RootState } from '@beam-app/types';
import {
  Callbacks,
  CriticalDataProduct,
  SearchBarExpansiveModel,
} from '@components/SearchBars/SearchBarExpansiveModel/SearchBarExpansiveModel';
import { getProductModels } from '@services/queries/products';
import { ProductCriticalData } from '@services/queries/products/getProductModels/getProductModels';
import { addKnownProduct } from '@store/redux/features/inventory/knownData';
import { useAppDispatch } from '@store/redux/store';
import { useRef, memo } from 'react';
import { useController } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { nullIfEmpty } from '@utils/nullIfEmpty';
import { BigCheckBoxInput } from '../BigCheckBoxInput';
import { BigInputDiv, BigInputRowDiv, InputList } from '../styles';
import { NOT_APPLICABLE } from './constants';
import { PropsInputSingle, FlattenSingle } from './types';
import {
  criticalToKnown,
  getSelectedValue,
  mapProductModels,
  onChangeSingle,
} from './utils';

type IdentificationInputSingleProps = PropsInputSingle &
  FlattenSingle<'productId' | 'serial'>;
function IdentificationInputSingle(props: IdentificationInputSingleProps) {
  const { viewForm, index, setFormV, onlyGroupEdits, productId, serial } =
    props;
  const knownProducts = useSelector(
    (state: RootState) => state.knownData.knownProducts,
  );
  const dispatch = useAppDispatch();

  const callbacksRef = useRef<Callbacks | null>(null);

  const {
    field: { onChange, value },
  } = useController({
    control: viewForm.control,
    name: `views.${index}.identificationSearchText`,
  });

  const {
    data,
    remove: removeQuery,
    refetch,
  } = useQuery(
    ['Product', 'Models', index],
    () => getProductModels(value as string),
    {
      enabled: callbacksRef.current !== null,
      onSettled() {
        if (callbacksRef.current) {
          callbacksRef.current.onSuccess(callbacksRef.current.key);
          callbacksRef.current = null;
        }
      },
    },
  );
  const mutationData: ProductCriticalData[] = value === null ? [] : data ?? [];
  const mutationDataMapped: CriticalDataProduct[] =
    mutationData.map(mapProductModels);

  const selectedValue: string | null = getSelectedValue(
    productId,
    knownProducts,
  );
  const productIdField = `equipments.${index}.productId` as const;
  const serialField = `equipments.${index}.serial` as const;

  return (
    <InputList>
      <BigInputDiv>
        <SearchBarExpansiveModel
          showOptions={mutationDataMapped}
          clearResults={() => removeQuery()}
          searchText={value}
          selectedValue={selectedValue}
          placeholder="Find a Model #"
          onChange={_value => {
            onChange(_value);
          }}
          onSearch={cbs => {
            callbacksRef.current = cbs;
            removeQuery();
            refetch();
          }}
          onCloseIconClick={() => {
            onChange(null);
            if (selectedValue) {
              onChangeSingle(productIdField, null, setFormV);
            }
          }}
          onSelect={input => {
            dispatch(addKnownProduct(criticalToKnown(input)));
            onChangeSingle(productIdField, input.id, setFormV);
          }}
          isActuallyDisabled={onlyGroupEdits}
        />
      </BigInputDiv>
      <BigInputRowDiv data-disabled={onlyGroupEdits}>
        <BigCheckBoxInput
          // Todo: Make a way for fixing this being empty string and not null
          showCheckbox={
            serial === NOT_APPLICABLE || serial === null || serial === ''
          }
          isDisabled={serial === NOT_APPLICABLE}
          placeholder="Serial #"
          value={serial ?? ''}
          onChange={ev => {
            const cleanValue = nullIfEmpty.setValueAs(ev.target.value);
            onChangeSingle(serialField, cleanValue, setFormV);
          }}
          isHiddenDisabled={onlyGroupEdits}
          onClick={() => {
            const flippedValue =
              serial === NOT_APPLICABLE ? null : NOT_APPLICABLE;
            onChangeSingle(serialField, flippedValue, setFormV);
          }}
        />
      </BigInputRowDiv>
    </InputList>
  );
}
export default memo(IdentificationInputSingle);
